import { motion } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { useI18n, CONTACT, LOGO_TRANSPARENT } from "@/lib/i18n";

const HERO_IMG =
  "https://images.unsplash.com/photo-1767950470198-c9cd97f8ed87?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjB2aWxsYSUyMHBvb2x8ZW58MHx8fHwxNzc4NDI2NzU1fDA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const { t } = useI18n();

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      className="relative min-h-[100svh] w-full overflow-hidden flex items-center"
      data-testid="hero-section"
    >
      <div className="absolute inset-0">
        <motion.img
          src={HERO_IMG}
          alt="Luxury Ibiza villa with infinity pool at dusk"
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/80" />
      </div>

      <div className="relative max-w-7xl mx-auto w-full px-6 md:px-12 pt-32 pb-24 md:py-32 text-sand-50">
        <div className="flex flex-col items-center text-center">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="block text-[9.5px] md:text-[10.5px] tracking-[0.55em] uppercase text-gold/85 mb-8 md:mb-10"
            data-testid="hero-kicker"
          >
            {t.hero.kicker}
          </motion.span>

          <motion.img
            src={LOGO_TRANSPARENT}
            alt="Villa Reset Ibiza — Luxury Villa Care & Full-Service Hospitality"
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="w-[210px] sm:w-[250px] md:w-[300px] lg:w-[340px] select-none pointer-events-none"
            draggable={false}
            data-testid="hero-logo"
          />

          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 1.4, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 md:mt-12 w-16 h-px bg-gold/55 origin-center"
            aria-hidden="true"
          />

          {/* visually hidden h1 for SEO */}
          <h1 className="sr-only" data-testid="hero-title">
            Villa Reset Ibiza — {t.hero.tagline}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.4 }}
            className="mt-8 max-w-[480px] text-[15px] md:text-base text-sand-50/75 leading-[1.7] font-light"
            data-testid="hero-sub"
          >
            {t.hero.body}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.6 }}
            className="mt-10 md:mt-12 flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto"
          >
            <button
              onClick={() => scrollToId("contact")}
              className="group relative inline-flex items-center justify-center bg-sand-50 text-ink-900 px-9 py-4 text-[10.5px] uppercase tracking-[0.35em] hover:bg-gold transition-all duration-500"
              data-testid="hero-request-button"
            >
              <span className="relative">{t.hero.ctaPrimary}</span>
            </button>
            <a
              href={`https://wa.me/${CONTACT.whatsappRaw}`}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-2 border border-sand-50/60 text-sand-50 px-9 py-4 text-[10.5px] uppercase tracking-[0.35em] hover:bg-sand-50 hover:text-ink-900 hover:border-sand-50 transition-all duration-500"
              data-testid="hero-whatsapp-button"
            >
              <MessageCircle size={13} strokeWidth={1.3} />
              {t.hero.ctaSecondary}
            </a>
          </motion.div>
        </div>
      </div>

      <button
        onClick={() => scrollToId("about")}
        className="hidden md:flex absolute bottom-8 right-12 flex-col items-center gap-3 text-sand-50/60 hover:text-gold transition-colors duration-500"
        aria-label="Scroll to next section"
        data-testid="hero-scroll-down"
      >
        <span className="text-[9.5px] tracking-[0.45em] uppercase rotate-180" style={{ writingMode: "vertical-rl" }}>
          {t.hero.scroll}
        </span>
        <ArrowDown size={14} strokeWidth={1.1} />
      </button>
    </section>
  );
}
